import { useState, useEffect, useRef } from "react"
import { useParams, Link } from "react-router-dom"
import { useSession } from "../../auth/session"
import { useDispatch } from "react-redux"
import { License, LicenseOrigin, licenseOriginLabels, licenseTypeLabels } from "../../api/models"
import Spinner from "../widgets/Spinner"
import { buildImageSrcSet } from "../widgets/imageSrcSet"
import { formatExpiryDate } from "../widgets/licenseCells"
import LockCountdown from "../widgets/LockCountdown"
import { useLicense } from "../../redux/licensesSlice"
import { useProduct } from "../../redux/productsSlice"
import { useActivation, revokeActivationThunk, renameDeviceThunk } from "../../redux/activationsSlice"
import { AppDispatch } from "../../redux/store"
import { backdropDismiss } from "../../utils/backdropDismiss"

interface RenameTarget {
  deviceId: string
  deviceName: string
}

function isLocked(license: License): boolean {
  return license.lockedUntil !== undefined && new Date(license.lockedUntil) > new Date()
}

function formatDate(d?: Date | string) {
  if (!d) return "-"
  const date = typeof d === "string" ? new Date(d) : d
  return date.toLocaleDateString()
}

export default function LicenseDetail() {
  const { id } = useParams<{ id: string }>()
  const session = useSession()
  const accessToken = session.accessToken || ""

  const { data: license, status: licenseStatus } = useLicense(id!, accessToken)
  const [renameTarget, setRenameTarget] = useState<RenameTarget | null>(null)

  if (license) {
    return (
      <div className="page">
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 16 }}>
          <h1 className="page-title" style={{ margin: 0 }}>
            License
            {isLocked(license) && <span className="badge-locked">Locked</span>}
          </h1>
          <div style={{ display: "flex", alignItems: "center", gap: 16 }}>
            {license.lockedUntil && <LockCountdown lockedUntil={license.lockedUntil} />}
            <ProductHeader productId={license.productId} />
          </div>
        </div>
        <div className="detail-grid" style={{ marginTop: 24 }}>
          <div className="detail-item">
            <span className="detail-label">Type</span>
            <span className="detail-value">{licenseTypeLabels[license.type]}</span>
          </div>
          <div className="detail-item">
            <span className="detail-label">Origin</span>
            <span className="detail-value">
              {/* handed over from another account: the ref is THEIR order or
                  redemption, not a page of ours */}
              {license.transferredAt != null ? (
                <span title={`Transferred to you on ${formatDate(license.transferredAt)}`}>Transferred</span>
              ) : license.origin === LicenseOrigin.Order && license.refId ? (
                <Link to={`/orders/${license.refId}`} className="card-link">{licenseOriginLabels[license.origin]}</Link>
              ) : license.origin === LicenseOrigin.Code && license.refId ? (
                <Link to={`/redemptions/${license.refId}`} className="card-link">{licenseOriginLabels[license.origin]}</Link>
              ) : (
                licenseOriginLabels[license.origin]
              )}
            </span>
          </div>
          <div className="detail-item">
            <span className="detail-label">Created</span>
            <span className="detail-value">{formatDate(license.createdAt)}</span>
          </div>
          <div className="detail-item">
            <span className="detail-label">{license.origin === LicenseOrigin.Subscription ? "Renews" :  "Expires"}</span>
            <span>{formatExpiryDate(license)}</span>
          </div>
          <div className="detail-item">
            <span className="detail-label">Max Activations</span>
            <span className="detail-value">{license.maxActivations}</span>
          </div>
        </div>

        <h2 className="section-title">Activations</h2>
        {license.activationIds.active.length === 0 && license.activationIds.revoked.length === 0 ? (
          <p className="empty-state">No activations yet</p>
        ) : (
          <div className="table-scroll">
            <table className="data-table">
              <thead>
                <tr>
                  <th>Device</th>
                  <th>Activated</th>
                  <th>Deactivated</th>
                </tr>
              </thead>
              <tbody>
                {license.activationIds.active.map((activationId) => <ActiveActivationRow key={activationId} activationId={activationId} onRename={setRenameTarget} />)}
                {license.activationIds.revoked.map((activationId) => <RevokedActivationRow key={activationId} activationId={activationId} />)}
              </tbody>
            </table>
          </div>
        )}
        {renameTarget && (
          <RenameDeviceDialog
            target={renameTarget}
            accessToken={accessToken}
            onClose={() => setRenameTarget(null)}
          />
        )}
      </div>
    )
  }

  if (licenseStatus === "error") {
    return (
      <div className="page">
        <p className="empty-state">Error Loading license…</p>
      </div>
    )
  }

  return (
    <div className="page">
      <p className="empty-state"><Spinner size={16} /> Loading license…</p>
    </div>
  )
}

function ProductHeader({ productId }: { productId: string }) {
  const session = useSession()
  const accessToken = session.accessToken || ""
  const { data: product } = useProduct(productId, accessToken)

  if (!product) {
    return (
      <span className="empty-state">
        <Spinner size={12} /> Loading…
      </span>
    )
  }

  const imageSrcSet = product.imageUrl ? buildImageSrcSet(product.imageUrl, product.availableWidths) : undefined

  return (
    <div className="license-detail-header">
      {product.logoUrl ? (
        <img
          src={product.logoUrl}
          srcSet={buildImageSrcSet(product.logoUrl, product.logoAvailableWidths)}
          sizes="200px"
          alt={product.name}
          className="product-logo-title"
        />
      ) : (
        <span className="page-title" style={{ margin: 0 }}>{product.name}</span>
      )}
      {product.imageUrl && (
        <img src={product.imageUrl} srcSet={imageSrcSet} sizes="120px" alt="" className="license-group-img" />
      )}
    </div>
  )
}

function ActiveActivationRow({ activationId, onRename }: { activationId: string, onRename: (target: RenameTarget) => void }) {
  const session = useSession()
  const accessToken = session.accessToken || ""
  const dispatch = useDispatch<AppDispatch>()
  const { data: activation, status: activationStatus } = useActivation(activationId, accessToken)
  const [busy, setBusy] = useState(false)

  const handleDeactivate = async () => {
    setBusy(true)
    try {
      await dispatch(revokeActivationThunk({ activationId, accessToken })).unwrap()
    } catch (err) {
      console.error("Deactivation failed:", err)
      setBusy(false)
    }
  }

  if (activation) {
    return (
      <tr key={activation.id}>
        <td>
          {activation.deviceName}
          <button
            className="card-link"
            style={{ background: "none", border: "none", cursor: "pointer", padding: 0, marginLeft: 8, verticalAlign: "middle" }}
            onClick={() => onRename({ deviceId: activation.deviceId, deviceName: activation.deviceName })}
            aria-label="Rename device"
            title="Rename device"
          >
            <svg width="13" height="13" viewBox="0 0 16 16" fill="currentColor" style={{ display: "block" }}>
              <path d="M12.146.146a.5.5 0 0 1 .708 0l3 3a.5.5 0 0 1 0 .708l-10 10a.5.5 0 0 1-.168.11l-5 2a.5.5 0 0 1-.65-.65l2-5a.5.5 0 0 1 .11-.168l10-10zM11.207 2.5 13.5 4.793 14.793 3.5 12.5 1.207 11.207 2.5zm1.586 3L10.5 3.207 4 9.707V10h.5a.5.5 0 0 1 .5.5v.5h.5a.5.5 0 0 1 .5.5v.5h.293l6.5-6.5zm-9.761 5.175-.106.106-1.528 3.821 3.821-1.528.106-.106A.5.5 0 0 1 5 12.5V12h-.5a.5.5 0 0 1-.5-.5V11h-.5a.5.5 0 0 1-.468-.325z" />
            </svg>
          </button>
        </td>
        <td>{formatDate(activation.activatedAt)}</td>
        <td>
          <button className="btn-small btn-danger" onClick={handleDeactivate} disabled={busy}>
            {busy ? "Deactivating…" : "Deactivate"}
          </button>
        </td>
      </tr>
    )
  }

  if (activationStatus === "error") {
    return (
      <tr>
        <td colSpan={3}>
          <div className="empty-state">
            Error loading activation
          </div>
        </td>
      </tr>
    )
  }

  return (
    <tr>
      <td colSpan={3}>
        <div className="empty-state">
          <Spinner size={12} /> Loading…
        </div>
      </td>
    </tr>
  )
}

function RevokedActivationRow({ activationId }: { activationId: string }) {
  const session = useSession()
  const accessToken = session.accessToken || ""
  const { data: activation, status: activationStatus } = useActivation(activationId, accessToken)

  if (activation) {
    return (
      <tr key={activation.id} className="revoked" style={{ opacity: 0.5 }}>
        <td>{activation.deviceName}</td>
        <td>{formatDate(activation.activatedAt)}</td>
        <td>{activation.deactivatedAt ? formatDate(activation.deactivatedAt) : ""}</td>
      </tr>
    )
  }

  if (activationStatus === "error") {
    return (
      <tr>
        <td colSpan={3}>
          <div className="empty-state">
            Error loading activation
          </div>
        </td>
      </tr>
    )
  }

  return (
    <tr>
      <td colSpan={3}>
        <div className="empty-state">
          <Spinner size={12} /> Loading…
        </div>
      </td>
    </tr>
  )
}

// Confirmation dialog for renaming a device. The name is device-scoped
// (stored per device+user, joined into every activation read), so the warning
// spells out that all activations of this device change together. On success
// renameDeviceThunk.fulfilled patches every cached activation of the device,
// so the visible rows update in place and the dialog just closes.
function RenameDeviceDialog({ target, accessToken, onClose }: {
  target: RenameTarget
  accessToken: string
  onClose: () => void
}) {
  const dialogRef = useRef<HTMLDialogElement>(null)
  const dispatch = useDispatch<AppDispatch>()
  const [deviceName, setDeviceName] = useState(target.deviceName)
  const [step, setStep] = useState<"confirm" | "renaming" | "error">("confirm")
  const [errorMessage, setErrorMessage] = useState("")

  useEffect(() => {
    dialogRef.current?.showModal()
  }, [])

  const trimmedName = deviceName.trim()
  const canRename = trimmedName !== "" && trimmedName !== target.deviceName && step !== "renaming"

  const handleRename = async () => {
    setStep("renaming")
    setErrorMessage("")
    try {
      await dispatch(renameDeviceThunk({ deviceId: target.deviceId, deviceName: trimmedName, accessToken })).unwrap()
      dialogRef.current?.close()
    } catch (err) {
      setErrorMessage(err instanceof Error ? err.message : "unknown error")
      setStep("error")
    }
  }

  return (
    <dialog
      ref={dialogRef}
      className="redeem-dialog"
      onClose={onClose}
      {...backdropDismiss(() => { if (step !== "renaming") dialogRef.current?.close() })}
    >
      <div className="redeem-dialog-content">
        <button className="redeem-dialog-close" onClick={() => dialogRef.current?.close()} aria-label="Close">&times;</button>
        <h2 className="redeem-dialog-title">Rename Device</h2>

        <p>
          Renaming{" "}
          <strong>{target.deviceName}</strong> will update every activation of this device across
          all your products and licenses. Your activations will remain valid but the device will be identified by the new name.
        </p>
        <div className="generate-codes-form">
          <label className="generate-codes-field">
            <span className="generate-codes-label">Device name</span>
            <input
              type="text"
              maxLength={255}
              value={deviceName}
              onChange={(e) => setDeviceName(e.target.value)}
              className="generate-codes-input"
              disabled={step === "renaming"}
              autoFocus
            />
          </label>
        </div>

        {step === "error" && errorMessage && (
          <p className="generate-codes-error">{errorMessage}</p>
        )}

        <div className="redeem-actions">
          <button onClick={() => dialogRef.current?.close()} disabled={step === "renaming"}>Cancel</button>
          <button
            className="btn-upgrade"
            disabled={!canRename}
            onClick={handleRename}
          >
            {step === "renaming" ? <><Spinner size={12} /> Renaming…</> : "Rename"}
          </button>
        </div>
      </div>
    </dialog>
  )
}


